import { useCallback, useContext, useRef } from "react";
import { GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { TableV2GridContext } from "../grid-store";
import { BaseTableData } from "../types";

interface UseGridReadyProps<T extends BaseTableData> {
  gridRef?: React.RefObject<AgGridReact<T>>;
  data: T[];
  actualHeaderHeight: number;
  autoFitRowsToHeight?: boolean;
  onGridReady?: (params: GridReadyEvent) => void;
  setRowHeight: (height: number) => void;
}

export const useGridReady = <T extends BaseTableData>({
  gridRef,
  data,
  actualHeaderHeight,
  autoFitRowsToHeight,
  onGridReady,
  setRowHeight,
}: UseGridReadyProps<T>) => {
  const gridContext = useContext(TableV2GridContext);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const handleGridReady = useCallback(
    (params: GridReadyEvent) => {
      if (gridContext && gridRef?.current?.api) {
        gridContext.gridApiRef.current = gridRef?.current.api;
        gridContext.tableStore?.setGridApiRef(gridContext.gridApiRef, gridContext.onColumnMoved);

        if (autoFitRowsToHeight) {
          const containerHeight = gridContainerRef.current?.offsetHeight;

          const numRows = data.length;
          const MAX_ROW_HEIGHT = 250;

          if (numRows > 0) {
            const rowHeight = Math.floor(((containerHeight || 0) - actualHeaderHeight) / numRows);

            setRowHeight(rowHeight > MAX_ROW_HEIGHT ? MAX_ROW_HEIGHT : rowHeight);
          }
        }
      }

      if (onGridReady) {
        onGridReady(params);
      }
    },
    [
      onGridReady,
      gridContext,
      data,
      actualHeaderHeight,
      autoFitRowsToHeight,
      gridRef,
      setRowHeight,
    ],
  );

  return {
    handleGridReady,
    gridContainerRef,
  };
};
